/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from '@scuf/localization';
import descriptors from '../../../../locales/en-US/descriptors';
import withErrorStatus from '../../../shared/StatusWrappers/ErrorStatus';
import withLoadingStatus from '../../../shared/StatusWrappers/LoadingStatus';
import ConnectionError from '../../../shared/ErrorComponents/ConnectionError';
import { isObjectOrArrayAndEmpty } from '../../../shared/Utils/Utils';
import {
  ICarbonData,
  IDetailComponentProps,
  IDetailComponentPropsTableSeparate,
  ISiteCard
} from '../../../DashboardKPIWidgets/IEnergyManagementProps';
import TotalEmissionsSavingsByMeterDetailComponent from './TotalEmissionsSavingsByMeterDetailComponent';
import TotalEmissionsSavingsByMeterDetailNewComponent from './TotalEmissionsSavingsByMeterDetailNewComponent';
import { Card } from '@scuf/common';

interface ITotalEmissionMeter extends ISiteCard {
  data: ICarbonData;
  toggleFeatureScopeOne?: boolean;
  onRefresh: () => Promise<void>;
}

const TotalEmissionsSavingsByMeterComponentContent = (
  props: ITotalEmissionMeter
) => {
  const d = descriptors.energyManagement;
  const [t] = useTranslation();
  const { data, onRefresh } = props;
  if (isObjectOrArrayAndEmpty(data)) {
    return (
      <div className="errorDisplay">
        <ConnectionError
          title={t(d.CONNECTIONERRORTITLE)}
          message={t(d.CONNECTIONERROR)}
          onRefresh={onRefresh}
        />
      </div>
    );
  }

  return (
    <div>
      {props.toggleFeatureScopeOne ? (
        <TotalEmissionsSavingsByMeterDetailNewComponent data={data} />
      ) : (
        <TotalEmissionsSavingsByMeterDetailComponent data={data} />
      )}
    </div>
  );
};

const TotalEmissionsSavingsByMeterComponentWithLoader = withLoadingStatus(
  withErrorStatus(TotalEmissionsSavingsByMeterComponentContent)
);

const TotalEmissionsSavingsByMeterComponentSeparate = (
  props: IDetailComponentPropsTableSeparate
) => {
  const [t] = useTranslation();
  const d = descriptors.energyManagement;
  const { dataConnectorTable, dataConnector } = props;

  const onRefreshTable = () =>
    dataConnectorTable &&
    dataConnectorTable.actions &&
    dataConnectorTable.actions.getAll();
  const allTable = dataConnectorTable.data.all;
  const loadingTable = dataConnectorTable.data.loading;
  const errorTable = dataConnectorTable.data.error;
  const statusTable = dataConnectorTable.data.status;

  const carbonTotalSavingsData = (allTable as unknown) as ICarbonData;
  const isDataAvailableTable =
    carbonTotalSavingsData && carbonTotalSavingsData.emissionsByUtility
      ? true
      : false;

  const [isLoadingTable, setIsLoadingTable] = useState<boolean>(loadingTable);
  const [statusCodeTable, setStatusCodeTable] = useState<number | null>(0);
  const [errorInfoTable, setErrorInfoTable] = useState(errorTable);
  const [dataTable, setDataTable] = useState({} as ICarbonData);

  useEffect(() => {
    setIsLoadingTable(loadingTable);
    if ((isDataAvailableTable || errorTable || statusTable) && !loadingTable) {
      if (isDataAvailableTable) {
        let tempData: any = carbonTotalSavingsData;
        setDataTable(tempData);
      }
      setErrorInfoTable(errorTable);
      setStatusCodeTable(statusTable);
    }
  }, [isDataAvailableTable, loadingTable, statusTable]);

  return (
    <Card className="Savemeter">
      <Card.Header className="emaas-carbon-view-details-total-emissions-savings-meter-card-header">
        {props.toggleFeatureScopeOne
          ? t(d.breakdownCarbonScopeOne)
          : t(d.breakdownCarbon)}
      </Card.Header>
      <Card.Content>
        <div className="meter-savings-Datatable detail-view">
          <TotalEmissionsSavingsByMeterComponentWithLoader
            loading={isLoadingTable}
            error={errorInfoTable}
            statusCode={statusCodeTable}
            onRefresh={onRefreshTable}
            data={dataTable}
            loadingText={t(d.LOADING)}
            title={t(d.CONNECTIONERRORTITLE)}
            noDataMessage={t(d.NODATAAVAILABLE)}
            connectionErrorMessage={t(d.CONNECTIONERROR)}
            toggleFeatureScopeOne={props.toggleFeatureScopeOne}
          />
        </div>
      </Card.Content>
    </Card>
  );
};
export default TotalEmissionsSavingsByMeterComponentSeparate;
